//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

import LOOP_LOGO from '../../icons/modern/loop_logo_color.svg';
import LOOP_LOGO_DARK from '../../icons/modern/loop_logo_dark_mode.png';
import LOOP_404 from '../../icons/modern/loop_404.webp';

function NotFound() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return createPortal(
    <div
      id="id-not-found-container"
      data-testid="not-found-container"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-carbon-light dark:bg-carbon-dark"
    >
      {/* Wrapper that holds both the card and illustration */}
      <div
        id="id-not-found-wrapper"
        data-testid="not-found-wrapper"
        className="relative flex items-start"
      >
        {/* Centre card */}
        <div
          id="id-not-found-card"
          data-testid="not-found-card"
          className="relative z-10 flex min-h-[688px] w-[776px] flex-col items-center rounded-lg bg-white px-8 py-10 shadow-sm dark:bg-carbon-darkPanel dark:shadow-md"
        >
          {/* Logo */}
          <img
            src={LOOP_LOGO}
            alt={t('not-found-logo-alt')}
            className="mb-8 h-[210px] w-[462px] max-w-full object-contain dark:hidden"
          />
          <img
            src={LOOP_LOGO_DARK}
            alt={t('not-found-logo-alt')}
            className="mb-8 hidden h-[210px] w-[462px] max-w-full object-contain dark:block"
          />

          {/* Divider */}
          <div
            id="id-not-found-divider"
            data-testid="not-found-divider"
            className="mb-8 w-full border-t border-gray-200 dark:border-gray-700"
          />

          {/* 404 heading */}
          <h1 className="mb-2 text-3xl font-bold leading-tight text-logout-text dark:text-gray-200">
            {t('not-found-heading')}
          </h1>

          {/* Message */}
          <p className="mb-6 text-center text-xl text-logout-text dark:text-gray-300">
            {t('not-found-message')}
          </p>

          {/* Action button */}
          <Button
            onClick={() => navigate('/')}
            className="rounded bg-brand-interactive px-4 py-1 text-sm text-white hover:bg-brand-blue dark:bg-brand-interactive dark:hover:bg-logout-cancel-dark"
          >
            {t('not-found-return-button')}
          </Button>
        </div>

        {/* 404 illustration — overlaps card right edge by ~44px, bottom-aligned with card */}
        <img
          src={LOOP_404}
          alt={t('not-found-illustration-alt')}
          className="pointer-events-none absolute bottom-0 left-[calc(100%-44px)] hidden h-[447px] w-[457px] select-none object-contain lg:block"
        />
      </div>
    </div>,
    document.body,
  );
}

export default NotFound;
